"use client";

import { useEffect, type ReactNode } from "react";
import { usePathname } from "next/navigation";
import { SessionProvider, useSession } from "next-auth/react";
import { ThemeComponent } from "@ui-kit/theme/theme-component";
import { NotificationProvider } from "@ui-kit";
import { initSnowplow, trackPageView } from "@integration/analytics/snowplow";
import { TRPCProvider } from "@integration/trpc/client";
import {
  SettingsConsumer,
  SettingsProvider,
} from "./(app)/_layout/settings-context";
import { ErrorBoundary } from "./(app)/_layout/error-boundary";
import { EmotionRegistry } from "./emotion-registry";
import { TrpcErrorNotifier } from "./trpc-error-notifier";

const SnowplowTracker = () => {
  const { data: session } = useSession();
  const pathname = usePathname();

  useEffect(() => {
    const userEmail = session?.user?.email;
    if (!userEmail) return;

    const isSnowplowEnabled = initSnowplow({ userEmail });
    if (!isSnowplowEnabled) return;

    trackPageView();
  }, [session?.user?.email, pathname]);

  return null;
};

export const Providers = ({ children }: { children: ReactNode }) => {
  return (
    <ErrorBoundary>
      <SessionProvider>
        <NotificationProvider>
          <TrpcErrorNotifier />
          <TRPCProvider>
            <EmotionRegistry>
              <SnowplowTracker />
              <SettingsProvider>
                <SettingsConsumer>
                  {({ settings }) => (
                    <ThemeComponent settings={settings}>
                      {children}
                    </ThemeComponent>
                  )}
                </SettingsConsumer>
              </SettingsProvider>
            </EmotionRegistry>
          </TRPCProvider>
        </NotificationProvider>
      </SessionProvider>
    </ErrorBoundary>
  );
};
